import { useMutation } from '@apollo/client';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useAppDispatch } from '../app/redux/useAppSelector';
import { gql } from '../graphql/generated/gql';
import { PostType } from '../graphql/generated/graphql';
import { UIActions } from '../ui/uiSlice';
import useNavigate from '../utils/router/useNavigate';
import postPath from '../utils/url/postPath';
import safeParseUrl from '../utils/url/safeParseUrl';

const CREATE_POST_MUTATION = gql(/** @complexity 2 */ `
  mutation createPost(
    $topicId: ID
    $libraryName: String
    $source: String
    $type: PostType
  ) {
    post: createPost(
      topicId: $topicId
      libraryName: $libraryName
      source: $source
      type: $type
    ) {
      id
      shareSecret
      ...postPath_post
    }
  }  
`);

const CREATE_POST_FROM_TEMPLATE = gql(/** @complexity 2 */ `
  mutation createPostFromTemplate($id: ID!) {
    post: duplicatePost(id: $id) {
      id
      shareSecret
      ...postPath_post
    }
  }
`);

const CreatePostView = () => {
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('templateId');
  const library = searchParams.get('library');
  const utmSource = searchParams.get('utm_source');
  const topicId = searchParams.get('topicId');
  const type = searchParams.get('type');

  let source = 'link';

  if (templateId) {
    source = 'template';
  } else if (library) {
    source = 'library';
  } else if (
    window.history.state ||
    safeParseUrl(document.referrer)?.hostname === document.location.hostname
  ) {
    source = 'internal';
  } else if (['slack'].includes(utmSource as string)) {
    source = utmSource as string;
  }

  const [createPost] = useMutation(CREATE_POST_MUTATION, {
    variables: {
      topicId,
      libraryName: library,
      source,
      type: type === 'template' ? PostType.Template : PostType.Post,
    },
  });

  const [createPostFromTemplate] = useMutation(CREATE_POST_FROM_TEMPLATE);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const promise = templateId
      ? createPostFromTemplate({ variables: { id: templateId } })
      : createPost();

    promise.then(({ data }) => {
      if (data?.post) {
        if (templateId) {
          dispatch(UIActions.addUsedTemplate(templateId));
        }

        navigate(
          postPath(data.post, {
            shareSecret: data.post.shareSecret ?? undefined,
          }),
          {
            replace: true,
          },
        );
      }
    });
  }, [createPost, dispatch, navigate, templateId, createPostFromTemplate]);

  return null;
};

export default CreatePostView;
